/**
 * ProcessAnalytics — SMB-grade analytics dashboard.
 *
 * Consumes /v1/processes/analytics (live backend, alembic 028+ engine).
 * Renders the 6 metrics SMB admins actually look at, modeled after
 * Process Street / Pipefy / Kissflow / Monday / Asana analytics:
 *
 *   1. KPI row — total / completed / pending / overdue / avg cycle
 *   2. Volume trend (14-day line chart)
 *   3. Status breakdown (donut)
 *   4. Top processes by volume (bar)
 *   5. Bottleneck nodes — where things get stuck
 *   6. Workload by assignee — who has most pending
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Card, CardContent, CircularProgress, Grid, IconButton,
  LinearProgress, Paper, Stack, Tooltip, Typography, Avatar, Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Timer as TimerIcon,
  WarningAmberOutlined,
  AssignmentOutlined,
  CheckCircleOutlined,
  HourglassEmptyOutlined,
} from '@mui/icons-material';
import { ApiError, organizationApi, processesApi } from '../../../api';
import type { ProcessAnalytics as ProcessAnalyticsData, ProcessOverview, ProcessDefinition } from '../../../api/processes';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';
import { fmtUnit } from '../../../utils/datetime';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ACCENT = '#1976d2';
const SUCCESS = '#2e7d32';
const WARNING = '#ed6c02';
const ERROR = '#d32f2f';

function formatHours(h: number | string | null | undefined): string {
  // Defensive: backend Decimal can serialize as string via Pydantic.
  const num = typeof h === 'number' ? h : parseFloat(String(h ?? 0));
  if (!Number.isFinite(num) || num <= 0) return '—';
  if (num < 1) return fmtUnit(Math.round(num * 60), 'minute', undefined, 0);
  if (num < 24) return fmtUnit(num, 'hour');
  return fmtUnit(num / 24, 'day');
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ProcessAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<ProcessOverview | null>(null);
  const [analytics, setAnalytics] = useState<ProcessAnalyticsData | null>(null);
  const [definitions, setDefinitions] = useState<ProcessDefinition[]>([]);
  const [workers, setWorkers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, an, defs, workerRows] = await Promise.all([
        processesApi.processAnalyticsApi.overview(),
        processesApi.processAnalyticsApi.analytics(),
        processesApi.processDefinitionsApi.list(),
        // `workload_by_assignee` returns `worker_id` and nothing else, so without this the
        // card printed `wrk_01KS7442…` truncated. An opaque token is worse than an absent
        // line: the reader cannot act on it and cannot tell it from a rendering bug. The
        // page already did exactly this for definition ids (`defNameMap`).
        organizationApi.workersApi.list('active'),
      ]);
      setOverview(ov);
      setAnalytics(an);
      setDefinitions(defs);
      setWorkers(workerRows as any[]);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('processes:processAnalytics.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build a lookup table for definition_id → name
  const defNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of definitions) m.set(d.id, d.name ?? d.id);
    return m;
  }, [definitions]);

  /** worker_id → the person's name, for the workload card. */
  const workerNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workers) {
      const name = [w.first_name, w.last_name].filter(Boolean).join(' ') || w.worker_code;
      if (w.id && name) m.set(w.id, name);
    }
    return m;
  }, [workers]);

  // Compute KPIs from overview + analytics
  const kpis = useMemo(() => {
    if (!overview || !analytics) return null;
    const kpis = (overview.kpis ?? {}) as any;
    const byStatus = (kpis.by_status ?? {}) as Record<string, number>;
    const total = Object.values(byStatus).reduce((acc, n) => acc + n, 0);
    const completed = byStatus.completed ?? 0;
    const failed = byStatus.failed ?? 0;
    const running = byStatus.running ?? 0;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Average cycle time across all definitions (weighted by volume)
    const cycleTimes = (analytics.avg_cycle_time_hours ?? {}) as Record<string, number | string>;
    const cycleVals = Object.values(cycleTimes).map((v) => typeof v === 'number' ? v : parseFloat(String(v ?? 0))).filter((n) => Number.isFinite(n));
    const avgCycle = cycleVals.length > 0
      ? cycleVals.reduce((a, b) => a + b, 0) / cycleVals.length
      : 0;

    return { total, completed, running, failed, completionRate, avgCycle, pendingTasks: kpis.pending_tasks ?? 0 };
  }, [overview, analytics]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '60vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('processes:analytics.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('processes:analytics.subtitle')}</Typography>
        </Box>
        <Tooltip title={t('common:actions.refresh')}>
          <IconButton size="small" onClick={fetchAll}
            sx={{ border: '1px solid rgba(0,0,0,0.15)', borderRadius: 1, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {/* KPI Row — 5 cards */}
      <Grid container spacing={2} sx={{ alignSelf: 'stretch', mb: 3 }}>
        <KpiCard
          icon={<AssignmentOutlined sx={{ color: ACCENT }} />}
          label={t('processes:processAnalytics.totalInstances')}
          value={kpis?.total ?? 0}
          color={ACCENT}
        />
        <KpiCard
          icon={<CheckCircleOutlined sx={{ color: SUCCESS }} />}
          label={t('processes:processAnalytics.completionRate')}
          value={`${kpis?.completionRate ?? 0}%`}
          subtitle={`${kpis?.completed ?? 0} ${t('processes:processAnalytics.completed')}`}
          color={SUCCESS}
        />
        <KpiCard
          icon={<HourglassEmptyOutlined sx={{ color: WARNING }} />}
          label={t('processes:processAnalytics.pendingTasks')}
          value={kpis?.pendingTasks ?? 0}
          subtitle={`${kpis?.running ?? 0} ${t('processes:processAnalytics.running')}`}
          color={WARNING}
        />
        <KpiCard
          icon={<TimerIcon sx={{ color: ACCENT }} />}
          label={t('processes:processAnalytics.avgCycleTime')}
          value={kpis ? formatHours(kpis.avgCycle) : '—'}
          subtitle={t('processes:processAnalytics.endToEnd')}
          color={ACCENT}
        />
        <KpiCard
          icon={<WarningAmberOutlined sx={{ color: ERROR }} />}
          label={t('processes:processAnalytics.failed')}
          value={kpis?.failed ?? 0}
          subtitle={t('processes:processAnalytics.needsAttention')}
          color={ERROR}
        />
      </Grid>

      {/* Row 1: Volume trend (line) + Top processes (bar) */}
      {/* 7/5, and the row below is 7/5 too. It used to be 7/5 here and 6/6 there, so the
          vertical seam between the left and right cards moved ~145px between the two rows —
          the eye reads that as two layouts stacked, which is what "左右卡片" was about. One
          split for every row on the page. */}
      <Grid container spacing={2} sx={{ alignSelf: 'stretch', mb: 3 }}>
        <Grid item xs={12} md={7}>
          <VolumeTrendCard trend={(analytics?.weekly_trend ?? []) as Array<{ date?: string | null; count: number }>} />
        </Grid>
        <Grid item xs={12} md={5}>
          <TopProcessesCard
            top={(analytics?.top_processes ?? []) as Array<{ definition_id: string; instance_count: number }>}
            defNameMap={defNameMap}
          />
        </Grid>
      </Grid>

      {/* Row 2: Bottlenecks + Workload */}
      <Grid container spacing={2} sx={{ alignSelf: 'stretch', mb: 3 }}>
        <Grid item xs={12} md={7}>
          <BottlenecksCard bottlenecks={(analytics?.bottlenecks ?? []) as Array<{ node_id: string; node_label?: string; pending_count: number; avg_age_hours?: number }>} />
        </Grid>
        <Grid item xs={12} md={5}>
          <WorkloadCard
            workload={(analytics?.workload_by_assignee ?? []) as Array<{ worker_id: string; pending_count: number }>}
            workerNameMap={workerNameMap}
          />
        </Grid>
      </Grid>

      {/* Row 3: Per-process performance */}
      <Box sx={{ width: '100%' }}>
        <ProcessPerformanceCard
          completion={(analytics?.completion_rates ?? {}) as Record<string, number>}
          cycleTimes={(analytics?.avg_cycle_time_hours ?? {}) as Record<string, number>}
          defNameMap={defNameMap}
        />
      </Box>
    </Box>
  );
};

export default ProcessAnalytics;

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------

const KpiCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
}> = ({ icon, label, value, subtitle, color }) => (
  <Grid item xs={12} sm={6} md={2.4}>
    {/* `height: '100%'` + a flex column: four of the five KPIs carry a caption line and one
        does not, so without this the bottom edges were ragged by the height of that line —
        the equal-height half of svdy-ui §11.2. Every other card on this page already had it;
        this was the one that did not. */}
    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)',
      height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 2, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {label}
          </Typography>
        </Stack>
        <Typography sx={{ fontWeight: 800, fontSize: '1.75rem', color: '#0a0a0a', lineHeight: 1.1, mb: 0.25 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Grid>
);

// ---------------------------------------------------------------------------
// Volume Trend (sparkline-style)
// ---------------------------------------------------------------------------

const VolumeTrendCard: React.FC<{
  trend: Array<{ date?: string | null; count: number }>;
}> = ({ trend }) => {
  const { t } = useTranslation();
  /**
   * A DENSE 14-day series. The line above used to be `trend.map((d) => d.count)` under a
   * comment reading "Pad to 14 days with 0 for missing" — the intent was written down and
   * never implemented.
   *
   * The endpoint returns only days that HAVE instances. With every instance on one day that is
   * a single data point, and a single `flex: 1` bar fills the whole card: the screenshot showed
   * one solid blue rectangle where a 14-day trend should be. A chart that contradicts its own
   * caption ("Instances started per day, last 14 days") is worse than no chart, because the
   * reader draws a conclusion from it.
   *
   * Keyed on the LOCAL date, matching how the buckets were built (the server sends a local day
   * boundary serialised as UTC), so the admin's own days line up with the labels.
   */
  const series = React.useMemo(() => {
    const localKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const counts = new Map<string, number>();
    for (const point of trend) {
      if (!point.date) continue;
      const when = new Date(point.date);
      if (Number.isNaN(when.getTime())) continue;
      counts.set(localKey(when), (counts.get(localKey(when)) ?? 0) + point.count);
    }
    const out: Array<{ key: string; count: number }> = [];
    const today = new Date();
    for (let back = 13; back >= 0; back -= 1) {
      const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() - back);
      const key = localKey(day);
      out.push({ key, count: counts.get(key) ?? 0 });
    }
    return out;
  }, [trend]);

  const data = series.map((d) => d.count);
  const max = Math.max(1, ...data);
  const total = data.reduce((a, b) => a + b, 0);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 0.25 }}>
              {t('processes:processAnalytics.volumeTrend')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('processes:processAnalytics.volumeTrendDesc')}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: ACCENT }}>
              {total}
            </Typography>
            <Typography variant="caption" color="text.secondary">{t('processes:processAnalytics.total')}</Typography>
          </Stack>
        </Stack>

        {/* Simple bar-based sparkline */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 100 }}>
          {data.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ width: '100%', textAlign: 'center', py: 4 }}>
              {t('processes:processAnalytics.noInstances14d')}
            </Typography>
          ) : (
            data.map((value, i) => (
              <Tooltip
                key={i}
                title={`${series[i]?.key ?? ''}: ${value}`}
                arrow
              >
                <Box
                  sx={{
                    flex: 1,
                    height: `${(value / max) * 100}%`,
                    minHeight: value > 0 ? 4 : 2,
                    bgcolor: value > 0 ? ACCENT : 'rgba(0,0,0,0.06)',
                    borderRadius: 0.5,
                    transition: 'all 0.15s ease',
                    '&:hover': { bgcolor: '#0d47a1' },
                  }}
                />
              </Tooltip>
            ))
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Top processes bar
// ---------------------------------------------------------------------------

const TopProcessesCard: React.FC<{
  top: Array<{ definition_id: string; instance_count: number }>;
  defNameMap: Map<string, string>;
}> = ({ top, defNameMap }) => {
  const { t } = useTranslation();
  const max = Math.max(1, ...top.map((t) => t.instance_count));

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 0.25 }}>
            {t('processes:processAnalytics.topProcesses')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('processes:processAnalytics.topProcessesDesc')}
          </Typography>
        </Box>

        {top.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            {t('processes:processAnalytics.noProcessActivity')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {top.slice(0, 5).map((row) => (
              <Box key={row.definition_id}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8125rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {defNameMap.get(row.definition_id) ?? row.definition_id.slice(0, 8)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: ACCENT, fontSize: '0.8125rem' }}>
                    {row.instance_count}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(row.instance_count / max) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    bgcolor: 'rgba(0,0,0,0.04)',
                    '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 1 },
                  }}
                />
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Bottleneck nodes
// ---------------------------------------------------------------------------

const BottlenecksCard: React.FC<{
  bottlenecks: Array<{ node_id: string; node_label?: string; pending_count: number; avg_age_hours?: number }>;
}> = ({ bottlenecks }) => {
  const { t } = useTranslation();
  return (
  <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', height: '100%' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <WarningAmberOutlined sx={{ fontSize: 18, color: WARNING }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
          {t('processes:processAnalytics.bottlenecks')}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {t('processes:processAnalytics.bottlenecksDesc')}
      </Typography>

      {bottlenecks.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          {t('processes:processAnalytics.noBottlenecks')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {bottlenecks.map((b) => (
            <Stack key={b.node_id} direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.25, borderRadius: 1, bgcolor: 'rgba(237,108,2,0.06)', border: '1px solid rgba(237,108,2,0.15)' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.node_label ?? b.node_id}
                </Typography>
                {b.avg_age_hours !== undefined && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {t('processes:processAnalytics.averageAge', { age: formatHours(b.avg_age_hours) })}
                  </Typography>
                )}
              </Box>
              <Chip
                label={t('processes:processAnalytics.pendingCount', { count: b.pending_count })}
                size="small"
                sx={{ bgcolor: WARNING, color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 22 }}
              />
            </Stack>
          ))}
        </Stack>
      )}
    </CardContent>
  </Card>
)
};

// ---------------------------------------------------------------------------
// Workload by assignee
// ---------------------------------------------------------------------------

const WorkloadCard: React.FC<{
  workload: Array<{ worker_id: string; pending_count: number }>;
  workerNameMap: Map<string, string>;
}> = ({ workload, workerNameMap }) => {
  const { t } = useTranslation();
  const max = Math.max(1, ...workload.map((w) => w.pending_count));

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 0.25 }}>
            {t('processes:processAnalytics.workloadByAssignee')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('processes:processAnalytics.workloadDesc')}
          </Typography>
        </Box>

        {workload.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            {t('processes:processAnalytics.noActiveAssignees')}
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {workload.slice(0, 8).map((w) => (
              <Stack key={w.worker_id} direction="row" alignItems="center" spacing={1.5}>
                {/* Initials from the NAME. They were `worker_id.slice(0, 2)`, which is the id
                    prefix — so every avatar on the card read "WR". */}
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(25,118,210,0.12)', color: ACCENT, fontSize: '0.75rem', fontWeight: 700 }}>
                  {(workerNameMap.get(w.worker_id) ?? '?')
                    .split(' ').filter(Boolean).slice(0, 2)
                    .map((part: string) => part[0]).join('').toUpperCase() || '?'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {workerNameMap.get(w.worker_id) ?? t('processes:processAnalytics.formerWorker')}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: ACCENT, fontSize: '0.75rem' }}>
                      {w.pending_count}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={(w.pending_count / max) * 100}
                    sx={{
                      height: 4,
                      borderRadius: 1,
                      bgcolor: 'rgba(0,0,0,0.04)',
                      '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 1 },
                    }}
                  />
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Per-process performance table
// ---------------------------------------------------------------------------

const ProcessPerformanceCard: React.FC<{
  completion: Record<string, number>;
  cycleTimes: Record<string, number>;
  defNameMap: Map<string, string>;
}> = ({ completion, cycleTimes, defNameMap }) => {
  const { t } = useTranslation();
  const rows = Object.keys(completion).map((defId) => ({
    defId,
    name: defNameMap.get(defId) ?? defId.slice(0, 12),
    completionRate: completion[defId] ?? 0,
    cycleHours: cycleTimes[defId] ?? 0,
  }));

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 0.25 }}>
            {t('processes:processAnalytics.perProcessPerformance')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('processes:processAnalytics.perProcessDesc')}
          </Typography>
        </Box>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            {t('processes:processAnalytics.noProcessData')}
          </Typography>
        ) : (
          // `auto-fill` + `minmax`, not a fixed column count: five cards in three fixed
          // columns leave a hole on the right of the second row (svdy-ui §11.4). Cards now
          // stretch to fill whatever row they land in, so the right edge is always flush.
          <Box sx={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.5 }}>
            {rows.map((r) => (
              <Paper
                key={r.defId}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 1, border: '1px solid rgba(0,0,0,0.08)' }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8125rem', display: 'block', mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.name}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                      {t('processes:processAnalytics.completion')}
                    </Typography>
                    <Typography sx={{
                      fontWeight: 800, fontSize: '1.125rem',
                      color: r.completionRate >= 90 ? SUCCESS : r.completionRate >= 70 ? WARNING : ERROR,
                    }}>
                      {r.completionRate}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                      {t('processes:processAnalytics.avgCycle')}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.125rem', color: ACCENT }}>
                      {r.cycleHours > 0 ? formatHours(r.cycleHours) : '—'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
